import React, { useContext, MouseEvent } from "react";
import styled, { ThemeContext } from "styled-components";

import AlertTriangleIcon from "icons/AlertTriangleIcon";
import LoadingIcon from "icons/LoadingIcon";
import CloudCheckIcon from "icons/CloudCheckIcon";
import CloudDownloadIcon from "icons/CloudDownloadIcon";
import CloudOffIcon from "icons/CloudOffIcon";
import CloudRefreshIcon from "icons/CloudRefreshIcon";
import TrashIcon from "icons/TrashIcon";

interface DownloadIndicatorIconProps {
  indicatorState: string;
  theme: any;
  progress: number | undefined;
  size: number | string;
  active: boolean;
  offline: boolean;
}

const DownloadIndicatorIcon = ({
  indicatorState,
  theme,
  progress,
  active,
  size,
  offline
}: DownloadIndicatorIconProps) => {
  const defaultColor = theme.darkMode ? "white" : "#9D9D9D";

  if (
    !(indicatorState === "downloaded" || indicatorState === "delete") &&
    offline
  ) {
    return <CloudOffIcon color={defaultColor} size={size} />;
  }

  switch (indicatorState) {
    case "cancel":
      return (
        <ProgressRing
          key="cancel"
          color="#BE0000"
          progress={progress || 0}
          size={size}
        >
          <ProgressCancel />
        </ProgressRing>
      );
    case "delete":
      return <TrashIcon color="#BE0000" size={size} />;

    case "downloaded":
      return <CloudCheckIcon color={theme.colors.primary} size={size} />;
    case "downloading":
      return (
        <ProgressRing
          color={theme.colors.primary}
          progress={progress || 0}
          size={size}
        />
      );
    case "retry":
      return <CloudRefreshIcon color={defaultColor} size={size} />;
    case "failed":
      return <AlertTriangleIcon color="#BE0000" size={size} />;
    case "aborting":
    case "initializing":
      return <LoadingIcon color="#BE0000" size={size} />;
    case "download":
    default:
      return <CloudDownloadIcon color={defaultColor} size={size} />;
  }
};

const DownloadIndicatorContainer = styled.button<{ onClick: any }>`
  font-size: 0;
  line-height: 0;
  padding: 0;
  background: none;
  border: none;
  outline: none;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

interface DownloadIndicatorProps {
  indicatorState: string;
  progress: number | undefined;
  onClick: (event: MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  size?: number;
  active: boolean;
  offline: boolean;
  disabled?: boolean;
}

export const DownloadIndicator: React.FC<DownloadIndicatorProps> = ({
  indicatorState,
  progress,
  onClick = () => {},
  size = 24,
  active,
  offline,
  disabled,
  ...props
}) => {
  const theme = useContext(ThemeContext);

  return (
    <DownloadIndicatorContainer
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      <DownloadIndicatorIcon
        indicatorState={indicatorState}
        theme={theme}
        progress={progress}
        size={size}
        active={active}
        offline={offline}
      />
    </DownloadIndicatorContainer>
  );
};

const ProgressCancel = () => (
  <path d="M12.7676 12.0611C12.7599 12.0534 12.7537 12.0442 12.7495 12.0341C12.7453 12.0239 12.7431 12.0131 12.7431 12.0021C12.7431 11.9911 12.7453 11.9803 12.7495 11.9701C12.7537 11.96 12.7599 11.9508 12.7676 11.9431L15.8568 8.8542C15.9506 8.76027 16.0033 8.63292 16.0032 8.50017C16.0031 8.36743 15.9503 8.24015 15.8563 8.14635C15.7624 8.05255 15.635 7.99991 15.5023 8C15.3695 8.00009 15.2423 8.05292 15.1485 8.14685L12.0596 11.2344C12.0519 11.2422 12.0427 11.2483 12.0325 11.2525C12.0224 11.2567 12.0115 11.2589 12.0006 11.2589C11.9896 11.2589 11.9787 11.2567 11.9686 11.2525C11.9585 11.2483 11.9493 11.2422 11.9415 11.2344L8.85267 8.14685C8.80622 8.10038 8.75108 8.06352 8.69039 8.03836C8.6297 8.01321 8.56464 8.00025 8.49894 8.00024C8.36626 8.0002 8.239 8.05288 8.14515 8.14668C8.05131 8.24049 7.99857 8.36772 7.99854 8.50041C7.9985 8.63309 8.05118 8.76036 8.14498 8.8542L11.2335 11.9431C11.2413 11.9508 11.2474 11.96 11.2516 11.9701C11.2558 11.9803 11.258 11.9911 11.258 12.0021C11.258 12.0131 11.2558 12.0239 11.2516 12.0341C11.2474 12.0442 11.2413 12.0534 11.2335 12.0611L8.14498 15.1503C8.09854 15.1968 8.0617 15.252 8.03657 15.3127C8.01144 15.3734 7.99852 15.4384 7.99854 15.5041C7.99857 15.6368 8.05131 15.764 8.14515 15.8579C8.19162 15.9043 8.24678 15.9411 8.30748 15.9663C8.36819 15.9914 8.43324 16.0043 8.49894 16.0043C8.63163 16.0043 8.75887 15.9515 8.85267 15.8577L11.9415 12.7685C11.9493 12.7607 11.9585 12.7546 11.9686 12.7504C11.9787 12.7462 11.9896 12.744 12.0006 12.744C12.0115 12.744 12.0224 12.7462 12.0325 12.7504C12.0427 12.7546 12.0519 12.7607 12.0596 12.7685L15.1485 15.8577C15.2423 15.9515 15.3695 16.0043 15.5022 16.0043C15.6349 16.0043 15.7621 15.9517 15.856 15.8579C15.9498 15.764 16.0026 15.6368 16.0026 15.5041C16.0026 15.3714 15.95 15.2442 15.8562 15.1503L12.7676 12.0611Z" />
);

interface ProcessRingProps {
  color: string;
  progress: number;
  stroke?: number;
  size: number | string;
}

const ProgressRing: React.FC<ProcessRingProps> = ({
  children,
  color,
  progress = 0,
  stroke = 2,
  size = 24
}) => {
  const radius = 12;
  const normalizedRadius = radius - stroke / 2; // divide in 2 since half is inside and half outsdie
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <svg height={size} width={size} fill={color} viewBox="0 0 24 24">
      <circle
        stroke={color}
        fill="transparent"
        strokeWidth={stroke}
        strokeDasharray={circumference + " " + circumference}
        strokeLinecap="round"
        style={{
          strokeDashoffset,
          transition: "stroke-dashoffset 0.35s",
          transform: "rotate(-90deg)",
          transformOrigin: "50% 50%"
        }}
        r={normalizedRadius}
        cx={radius}
        cy={radius}
      />
      {children}
    </svg>
  );
};
